<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import axiosInstance from '@/plugins/axios';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const token = ref(route.query.token as string || '');
const password = ref('');
const confirmPassword = ref('');
const loading = ref(false);
const success = ref(false);
const redirectCountdown = ref(3);

onMounted(() => {
  if (!token.value) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: 'Kein Reset-Token gefunden.', life: 5000 });
  }
});

const submit = async () => {
  if (!password.value || !confirmPassword.value) {
    toast.add({ severity: 'warn', summary: 'Hinweis', detail: 'Bitte beide Felder ausfüllen.', life: 4000 });
    return;
  }
  if (password.value !== confirmPassword.value) {
    toast.add({ severity: 'warn', summary: 'Hinweis', detail: 'Die Passwörter stimmen nicht überein.', life: 4000 });
    return;
  }
  if (password.value.length < 8) {
    toast.add({ severity: 'warn', summary: 'Hinweis', detail: 'Das Passwort muss mindestens 8 Zeichen lang sein.', life: 4000 });
    return;
  }

  loading.value = true;
  try {
    await axiosInstance.post('/auth/reset-password', { token: token.value, password: password.value });
    success.value = true;
    const interval = setInterval(() => {
      redirectCountdown.value--;
      if (redirectCountdown.value <= 0) {
        clearInterval(interval);
        router.push('/login');
      }
    }, 1000);
  } catch (error: any) {
    const msg = error.response?.data?.message || 'Der Link ist ungültig oder abgelaufen.';
    toast.add({ severity: 'error', summary: 'Fehler', detail: msg, life: 6000 });
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="vh-100 d-flex align-items-center justify-content-center">
    <div class="surface-card p-5 shadow-2 border-round" style="max-width: 500px; min-width: 350px;">
      <ProgressBar v-if="loading" mode="indeterminate" class="mb-5" style="height: 6px" />

      <div class="text-center mb-5">
        <div class="text-900 text-3xl font-medium mb-1">Neues Passwort</div>
        <div class="text-sm font-medium mb-1">AIRiN Admin-Center</div>
      </div>

      <div v-if="!success">
        <div v-if="!token" class="text-center">
          <p class="text-red-500 mb-4">Kein gültiger Reset-Token gefunden. Bitte fordern Sie einen neuen Link an.</p>
          <a class="font-medium text-blue-500 cursor-pointer" @click="router.push('/forgot-password')">
            Neuen Link anfordern
          </a>
        </div>

        <div v-else>
          <label for="password" class="block text-900 font-medium mb-2">Neues Passwort</label>
          <Password
            fluid
            id="password"
            v-model="password"
            toggleMask
            :feedback="true"
            class="w-full mb-3"
          />

          <label for="confirmPassword" class="block text-900 font-medium mb-2">Passwort bestätigen</label>
          <Password
            fluid
            id="confirmPassword"
            v-model="confirmPassword"
            toggleMask
            :feedback="false"
            class="w-full mb-4"
            @keyup.enter="submit"
          />

          <Button
            label="Passwort zurücksetzen"
            icon="pi pi-lock"
            class="w-full mb-3"
            :loading="loading"
            @click="submit"
          />
        </div>
      </div>

      <div v-else class="text-center">
        <i class="pi pi-check-circle text-green-500 mb-3" style="font-size: 3rem;" />
        <p class="text-700 mb-2">Ihr Passwort wurde erfolgreich zurückgesetzt.</p>
        <p class="text-500">Sie werden in {{ redirectCountdown }} Sekunden weitergeleitet...</p>
      </div>

      <div class="text-center mt-3" v-if="!success">
        <a class="font-medium text-blue-500 cursor-pointer" @click="router.push('/login')">
          Zurück zur Anmeldung
        </a>
      </div>
    </div>
  </div>
  <div class="background-overlay" />
</template>

<style scoped>
.vh-100 { height: 100vh; }
.d-flex { display: flex; }
.align-items-center { align-items: center; }
.justify-content-center { justify-content: center; }

.background-overlay {
  width: 100%;
  height: 100%;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 1;
  background: black !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.surface-card {
  background-color: white !important;
  color: var(--tuev-dark-blue);
  z-index: 2;
}

.p-button {
  background-color: var(--tuev-light-blue);
  border-color: var(--tuev-light-blue);
}

.p-button:hover {
  background-color: var(--tuev-dark-blue) !important;
  border-color: var(--tuev-dark-blue) !important;
}
</style>
